import React, { useState } from "react";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Phone, MessageSquare } from "lucide-react";
import { toast } from "sonner";
import apiClient from "@/lib/apiClient";

interface PhoneVerificationDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

const PhoneVerificationDialog: React.FC<PhoneVerificationDialogProps> = ({
  isOpen,
  onClose,
  onSuccess,
}) => {
  const [step, setStep] = useState<1 | 2>(1); // 1: 電話番号入力, 2: 認証コード入力
  const [phoneNumber, setPhoneNumber] = useState<string>("");
  const [verificationCode, setVerificationCode] = useState<string>("");
  const [loading, setLoading] = useState<boolean>(false);

  const handleSendCode = async () => {
    // バリデーション
    if (!phoneNumber) {
      toast.error("電話番号を入力してください");
      return;
    }

    // 数字のみチェック
    const numbersOnly = phoneNumber.replace(/[^0-9]/g, "");
    if (numbersOnly.length !== 10 && numbersOnly.length !== 11) {
      toast.error("電話番号は10桁または11桁で入力してください");
      return;
    }

    setLoading(true);
    try {
      await apiClient.post("/phone_verifications", {
        phone_number: numbersOnly,
        method: "sms"
      });
      toast.success("認証コードを送信しました");
      setPhoneNumber(numbersOnly);
      setStep(2);
    } catch (error: any) {
      if (error.response?.data?.error) {
        toast.error(error.response.data.error);
      } else {
        toast.error("認証コードの送信に失敗しました");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyCode = async () => {
    if (!verificationCode) {
      toast.error("認証コードを入力してください");
      return;
    }

    if (verificationCode.length !== 6) {
      toast.error("認証コードは6桁で入力してください");
      return;
    }

    setLoading(true);
    try {
      await apiClient.post("/phone_verifications/verify", {
        phone_number: phoneNumber,
        code: verificationCode
      });
      toast.success("電話番号を認証しました");
      onSuccess();
    } catch (error: any) {
      if (error.response?.data?.error) {
        toast.error(error.response.data.error);
      } else {
        toast.error("認証に失敗しました");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setLoading(true);
    try {
      await apiClient.post("/phone_verifications", {
        phone_number: phoneNumber,
        method: "sms"
      });
      toast.success("認証コードを再送信しました");
    } catch (error: any) {
      if (error.response?.data?.error) {
        toast.error(error.response.data.error);
      } else {
        toast.error("認証コードの再送信に失敗しました");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setStep(1);
    setPhoneNumber("");
    setVerificationCode("");
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && handleClose()}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Phone className="h-5 w-5 text-blue-500" />
            電話番号認証
          </DialogTitle>
          <DialogDescription>
            {step === 1 ? (
              <>
                本人確認のため電話番号の認証が必要です。
                <br />
                <span className="text-gray-500 text-sm">
                  SMS で認証コードを送信します
                </span>
              </>
            ) : (
              <>
                {phoneNumber} に認証コードを送信しました。
                <br />
                <span className="text-gray-500 text-sm">
                  SMS に記載された6桁のコードを入力してください
                </span>
              </>
            )}
          </DialogDescription>
        </DialogHeader>

        {step === 1 ? (
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="phone">電話番号</Label>
              <Input
                id="phone"
                type="tel"
                placeholder="09012345678"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                maxLength={11}
              />
              <p className="text-xs text-gray-500">
                ハイフンなしで入力してください
              </p>
            </div>
          </div>
        ) : (
          <div className="grid gap-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="code">認証コード</Label>
              <Input
                id="code"
                type="text"
                placeholder="123456"
                value={verificationCode}
                onChange={(e) => setVerificationCode(e.target.value.replace(/[^0-9]/g, ""))}
                maxLength={6}
              />
              <div className="flex items-center justify-between">
                <p className="text-xs text-gray-500">
                  6桁の数字を入力してください
                </p>
                <button
                  onClick={handleResendCode}
                  disabled={loading}
                  className="text-xs text-blue-500 hover:underline disabled:opacity-50"
                >
                  再送信
                </button>
              </div>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={loading}
          >
            キャンセル
          </Button>
          {step === 1 ? (
            <Button
              onClick={handleSendCode}
              disabled={loading}
            >
              {loading ? "送信中..." : "認証コードを送信"}
            </Button>
          ) : (
            <Button
              onClick={handleVerifyCode}
              disabled={loading}
            >
              {loading ? "認証中..." : "認証する"}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PhoneVerificationDialog;
